/**
 * Payment refusals come back as Spring `ResponseStatusException` bodies, not as
 * the `{ success: false }` envelope used elsewhere, so the HTTP status is the
 * only reliable carrier of meaning. Each status maps to one i18n key.
 *
 * The key travels rather than the sentence: the data layer stays free of `t()`,
 * and the same failure can read differently in the declaration dialog and in
 * the admin queue.
 */
export class PaymentError extends Error {
  constructor(
    readonly status: number,
    readonly i18nKey: string,
  ) {
    super(i18nKey);
    this.name = "PaymentError";
  }
}

/**
 * `422` covers three distinct backend causes (unknown tier, not an upgrade,
 * renewal with no tier to derive) under one message: the pickers already
 * prevent all three, so a `422` means the client and the catalog have drifted
 * and a precise sentence would be a guess.
 */
const STATUS_KEYS: Record<number, string> = {
  400: "payments.errors.missingProof",
  403: "payments.errors.forbidden",
  404: "payments.errors.notFound",
  409: "payments.errors.alreadyPending",
  413: "payments.errors.tooLarge",
  415: "payments.errors.unsupportedType",
  422: "payments.errors.invalidTier",
};

export const PAYMENT_GENERIC_ERROR_KEY = "payments.errors.generic";

export const paymentErrorKey = (status: number): string =>
  STATUS_KEYS[status] ?? PAYMENT_GENERIC_ERROR_KEY;

export const paymentErrorFrom = (status: number): PaymentError =>
  new PaymentError(status, paymentErrorKey(status));

/** Narrows an unknown rejection to the i18n key it should be rendered as. */
export const resolvePaymentErrorKey = (error: unknown): string =>
  error instanceof PaymentError ? error.i18nKey : PAYMENT_GENERIC_ERROR_KEY;
